import { SnakeToCamel } from 'utils/caseTransformers';
import {
  CONNECTION_HEALTH,
  PR_KNOWLEDGE_STATUSES,
  REPO_STATUSES,
  REPOSITORY_OPERATIONS,
  SYNC_ERROR_TYPES,
  SYNC_STATUSES,
  TEST_CONNECTION_STATUS,
  TRACKING_MODE,
  WEBHOOK_HEALTH,
} from './consts';
import type { RequestSelection } from 'components/BulkActions';

/*************************
 *    Snaked case API
 ************************/
export interface SnakedGitOrganization {
  id: string;
  parent_organization_id: string | null;
  provider: string;
  git_provider_org_id: string;
  org_slug: string;
  depth: number;
  active: boolean;
  tenant: string;
  icon: string;
  created_at: string;
  updated_at: string;
}

/*************************
 *     Camel case API
 *************************/

export type GitOrganization = SnakeToCamel<SnakedGitOrganization>;

/******************************
 *         GET GitInstallations
 ******************************/

export type GetGitInstallationsRequest = {
  page?: number;
  pageSize?: number;
  search?: string;
  provider?: string | null;
  active?: boolean;
};

export type SyncStatus = (typeof SYNC_STATUSES)[keyof typeof SYNC_STATUSES];

export type RepoStatus = (typeof REPO_STATUSES)[keyof typeof REPO_STATUSES];

export type SyncErrorType =
  (typeof SYNC_ERROR_TYPES)[keyof typeof SYNC_ERROR_TYPES];

export type WebhookHealth =
  (typeof WEBHOOK_HEALTH)[keyof typeof WEBHOOK_HEALTH];

export type ConnectionHealth =
  (typeof CONNECTION_HEALTH)[keyof typeof CONNECTION_HEALTH];

export type TrackingMode = (typeof TRACKING_MODE)[keyof typeof TRACKING_MODE];

export type PrKnowledgeStatus =
  (typeof PR_KNOWLEDGE_STATUSES)[keyof typeof PR_KNOWLEDGE_STATUSES];

/**
 * Which PR-knowledge-consuming features this repo's data unlocks. Computed
 * by the platform (never derived client-side) so every surface renders the
 * same answer to "why isn't feature X firing on this repo".
 */
export type PrKnowledgeUnlocks = {
  ruleMiner: boolean;
  reviewRecommendations: boolean;
  developerPersonas: boolean;
};

/**
 * Per-repo PR Knowledge indexing state, enriched by the platform from the
 * qodo-engine workspace stats. `null` on the parent repository means the
 * enrichment was unavailable (engine down/disabled) — render a neutral
 * placeholder, never an affirmative status or an error; `status: 'not_started'`
 * is the distinct "engine doesn't know the repo" case that does show a badge.
 * `provider` is the provider the engine indexed the repo under; `lastActivity`
 * is the last indexing write, not a run-completed timestamp.
 */
export type PrKnowledgeInfo = {
  status: PrKnowledgeStatus;
  totalPrs: number;
  prsFullyIndexed: number;
  commentsIndexedPrs: number;
  fixSuggestionsCount: number;
  provider?: string | null;
  firstMergedAt?: string | null;
  lastMergedAt?: string | null;
  lastActivity?: string | null;
  /** Optional defensively — responses are not runtime-validated, so a partial payload must not crash the UI. */
  unlocks?: PrKnowledgeUnlocks;
};

export type RepositoryOperation =
  (typeof REPOSITORY_OPERATIONS)[keyof typeof REPOSITORY_OPERATIONS];

export type RepositoryOperationRequest = {
  operation: RepositoryOperation;
};

/**
 * 202 acknowledgement of an accepted operation. `status` is always `queued` —
 * accepted means the run was enqueued, not that it is executing; the repo's
 * `prKnowledge.status` reports the real progress from there.
 */
export type RepositoryOperationResponse = {
  operation: string;
  repoId: string;
  status: string;
};

export interface GitInstallation {
  id: string;
  name: string;
  provider: string;
  providerInstallationId: string;
  active: boolean;
  lastSyncedAt?: string;
  syncStatus?: SyncStatus;
  /** Classification of the most recent failed sync. Cleared (undefined) on success or in-progress. */
  errorType?: SyncErrorType;
  /** Human-readable reason for the most recent failed sync. Cleared (undefined) on success or in-progress. */
  errorDetail?: string;
  createdAt: string;
  updatedAt: string;
  avatarUrl?: string;
  repositoriesCount: number;
  connectionConfig?: Record<string, unknown>;
  /** GitLab-only: 'group_webhook' = one group hook covers all projects (legacy installs); 'per_project' = per-repo hooks. Null for non-GitLab. */
  trackingMode?: TrackingMode | null;
  /** GitLab install generation: 2 = new-wizard (v2) install, 1 = legacy/non-GitLab. Manage/Delete route by this. */
  installVersion?: number;
  /** GitLab-only: the instance base URL (e.g. https://gitlab.com); null when none recorded. v2 Manage uses it to deep-link the "create a token" page to the right instance. */
  baseUrl?: string | null;
}

export interface GetGitInstallationsResponse {
  total: number;
  page: number;
  pageSize: number;
  items: GitInstallation[];
}

/************************************
 *         GET GitInstallationsCount
 ************************************/

export const HOSTING_TYPES = {
  SAAS: 'saas',
  SELF_HOSTED: 'self_hosted',
} as const;

export type HostingType = (typeof HOSTING_TYPES)[keyof typeof HOSTING_TYPES];

/************************************
 *     GET InstallationsCounts (all)
 ************************************/

export type InstallationsCountsResponse = {
  github: number;
  gitlab: number;
  azure: number;
  bitbucket: number;
  bitbucket_datacenter: number;
  gerrit: number;
};

/******************************
 *         GET GitRepositories
 ******************************/

/** Backend-supported sort fields (git_integration RepositorySortField). */
export type RepositorySortField =
  | 'full_name'
  | 'created_at'
  | 'updated_at'
  | 'pr_knowledge';

export type GetGitRepositoriesRequest = {
  page?: number;
  pageSize?: number;
  search?: string;
  view?: 'minimal' | 'full';
  provider?: string | string[];
  installationId?: string | string[];
  sort?: RepositorySortField;
  sortDirection?: 'asc' | 'desc';
  // Filter by capability state (e.g. code review on/off), server-side across
  // the full result set. `enabled` requires `capability` to be set.
  capability?: 'code_review';
  enabled?: boolean;
};

export interface GetGitRepositoriesResponse {
  total: number;
  page: number;
  pageSize: number;
  items: GitRepository[];
}

export interface GitRepository {
  createdAt: string;
  updatedAt: string;
  id: string;
  provider: string;
  githubInstallationId?: string;
  providerInstallationId?: string;
  oauthUserId?: string;
  providerRepoId: string;
  fullName: string;
  name: string;
  /**
   * Provider-side org/namespace path (GitHub org, GitLab group full path
   * including subgroups, Azure org). Authoritative org grouping for the scope
   * UI — the org is not derivable from `fullName`'s first segment. Nullable:
   * the backing column predates this feature and isn't populated everywhere.
   */
  gitOrganization?: string | null;
  description?: string;
  htmlUrl: string;
  cloneUrl?: string;
  defaultBranch?: string;
  private: boolean;
  isDeleted: boolean;
  qodoEnabled: boolean;
  lastSyncedAt?: string;
  installation: GitInstallation | null;
  status?: RepoStatus;
  capabilities?: {
    codeReviewEnabled: boolean;
    contextEngineEnabled: boolean;
  };
  /**
   * Health of the repository's webhook. Null/undefined for non-GitLab repos
   * or repos with no webhook.
   *
   * @deprecated Prefer `connectionHealth` — the normalized, provider-agnostic
   * field. Retained as an alias during the backend rollout.
   */
  webhookHealth?: WebhookHealth | null;
  /**
   * Normalized, provider-agnostic connection health. Present for every
   * provider; `unknown` when there is no signal yet. Falls back to
   * `webhookHealth` on older backends that don't return this field.
   */
  connectionHealth?: ConnectionHealth | null;
  /**
   * PR Knowledge indexing state. `null` = enrichment unavailable (engine
   * down/disabled); absent on backends that predate the enrichment.
   */
  prKnowledge?: PrKnowledgeInfo | null;
  /**
   * Whether this repo may be selected as a cross-repo relation / rule scope.
   * Backend-owned eligibility flag — `false` for `disabled` repos (inactive
   * installation). Pickers read this directly instead of re-deriving from
   * connectionHealth. Absent on older backends → treated as selectable.
   */
  selectable?: boolean;
}

/*************************
 *         DELETE
 *************************/
export type DeleteGitInstallationRequest = {
  installationId: string;
};

export type DeleteGitInstallationResponse = {
  warning?: string;
};

/*************************
 *         SYNC
 *************************/
export type SyncGitInstallationRequest = {
  installationId: string;
};

/****************************************
 *   INSTALLATION REPOSITORIES (generic)
 ****************************************/

/**
 * Add/remove repositories on an existing installation via the generic
 * installation-repositories endpoint.
 * @property installationId - The `GitInstallation` id (the installation row's id, NOT providerInstallationId).
 * @property providerRepoIds - The provider repo ids to add/remove (e.g. GitLab project ids).
 */
export type InstallationRepositoriesRequest = {
  installationId: string;
  providerRepoIds: number[];
};

export type InstallationRepositoryResult = {
  providerRepoId: number;
  error?: string;
};

export type InstallationRepositoriesResponse = {
  succeeded: InstallationRepositoryResult[];
  failed: InstallationRepositoryResult[];
};

/*************************
 *       Bitbucket
 *************************/
export type BitbucketConfigResponse = {
  isMultiTenant: boolean;
  callbackUrl: string;
  oauthPopupBaseUrl: string;
  oauthApiBaseUrl: string;
  oauthClientId?: string;
  oauthAuthorizeUrl?: string;
  oauthScopes?: string;
};

export type BitbucketOAuthSessionCreateRequest = {
  token: string;
};

export type BitbucketOAuthSessionCreateResponse = {
  sessionId: string;
};

export type BitbucketWorkspaceInfo = {
  slug: string;
  name: string;
  uuid: string;
};

export type BitbucketOAuthSessionResponse = {
  authenticatedUser: string;
  bitbucketUserId: string;
  workspaces: BitbucketWorkspaceInfo[];
  scopes: string[];
};

export type BitbucketCreateInstallationRequest = {
  workspaceSlug: string;
  workspaceName: string;
  sessionId: string;
};

export type BitbucketCreateInstallationResponse = {
  installationId: string;
  installationStatus: string;
  name: string;
  bitbucketWorkspaceSlug: string;
  alreadyExists: boolean;
  allowedActions: string[];
};

export type BitbucketWizardSelectionOption = {
  providerRepoId: string;
  name: string;
};

export type BitbucketWizardSelectionOptionsResponse = {
  items: BitbucketWizardSelectionOption[];
};

export type BitbucketInstallWebhooksRequest = {
  bitbucketWorkspaceSlug: string;
  providerRepoIds: string[];
};

export type BitbucketWebhookResult = {
  providerRepoId: string;
  repositoryId: string;
  repositoryName: string;
  status: 'installed' | 'failed' | 'error';
  error?: string;
};

export type BitbucketInstallWebhooksResponse = {
  results: BitbucketWebhookResult[];
  installationStatus: string;
  successCount: number;
  failureCount: number;
};

export type BitbucketReinstallWebhooksRequest = {
  bitbucketWorkspaceSlug: string;
};

// The backfill is dispatched asynchronously, so the response reports whether a
// background job started rather than per-repo results. `started === false`
// means nothing was missing. Poll the installation for the final status.
export type BitbucketReinstallWebhooksResponse = {
  started: boolean;
  missingCount: number;
  installationStatus?: string | null;
};

/*************************
 * Gerrit is connected via LiteGit — the platform holds no Gerrit secret, and
 * the LiteGit credential id never reaches the client. `POST /gerrit/installations`
 * validates the credentials, creates the installation, and auto-starts a
 * server-side import of all its repos; the client polls `import-status` for
 * progress. An abandoned installation is torn down via the generic delete.
 *************************/
export type GerritCreateInstallationRequest = {
  baseUrl: string;
  username: string;
  token: string;
  /**
   * Path to Gerrit's authenticated REST API, prepended to every API call and to
   * the clone URL. Omit to let the backend apply its default (`/a`). A server
   * hosted under a context path (`https://host/r/`) belongs in `baseUrl`, not
   * here — browse links are built from `baseUrl` alone.
   */
  apiPrefix?: string;
};

export type GerritCreateInstallationResponse = {
  /** The real platform installation created from the validated credentials. */
  installationId: string;
  /** Qodo webhook URL to register on the Gerrit server; null until the backend can resolve it. */
  webhookUrl: string | null;
};

/**
 * Live status of the server-side auto-import, reusing the standard `SyncStatus`.
 * `error` is set only when `status` is `SYNC_STATUSES.FAILED`.
 */
export type GerritImportStatusResponse = {
  status: SyncStatus;
  savedCount: number;
  error?: string;
};

/*************************
 *    TEST CONNECTION
 *************************/
export type TestConnectionStatus =
  (typeof TEST_CONNECTION_STATUS)[keyof typeof TEST_CONNECTION_STATUS];

export interface TestConnectionResponse {
  status: TestConnectionStatus;
  message: string;
}

/*************************
 *    WEBHOOK URL
 *************************/
export type GetWebhookUrlResponse = {
  webhookUrl: string | null;
};

/*************************
 *    MT INSTALLATION
 *************************/
export interface InitiateMTGithubInstallationResponse {
  installationUrl: string;
}

export interface CreateMarketplaceMTGithubInstallationRequest {
  installationId: number;
  state: string;
}

export interface CreateMarketplaceMTGithubInstallationResponse {
  installationId: string;
}

/*************************
 *    BULK CAPABILITIES
 *************************/
/**
 * Repository bulk operations against POST /git_integration/v1/repositories/bulk.
 * Code review only — the context-engine capability is deliberately not bulk-
 * togglable, so scoping review can never change what a repo contributes as
 * cross-repo context. Stays a per-row toggle.
 */
export type BulkRepositoryOperation =
  | 'enable_code_review'
  | 'disable_code_review';

export interface BulkRepositoriesRequest {
  operation: BulkRepositoryOperation;
  selection: RequestSelection;
  dryRun?: boolean;
}

/** The filter an `all`-mode selection resolves against: the list query, minus paging. */
export interface BulkRepositoriesFilter {
  search?: string;
  provider?: string[];
  installationId?: string[];
}

/** Status counts for one task type, over each subject's latest task. */
export type TrackerTaskTypeSummary = {
  taskType: string;
  counts: Record<string, number>;
  /** Sums of the type's numeric metadata keys (writer-defined). */
  metricTotals?: Record<string, number>;
};

export type TaskTrackerSummaryResponse = {
  summaries: TrackerTaskTypeSummary[];
};
